import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Cloud, Rocket, CheckCircle2, XCircle, RefreshCw, Save, Wifi, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import ReenvioExpansion from "./ReenvioExpansion";
import StockShopifyPanel from "./StockShopifyPanel";

const ESTADO_CHIP = { pendiente: "chip-amber", enviado: "chip-emerald", error: "chip-red", fallido: "chip-red" };

const PRESETS = {
  rubinova: {
    Icon: Cloud,
    titulo: "Integración RubiNOVA · Sincronización de datos",
    desc: "RubiPOS envía automáticamente a RubiNOVA: ventas, movimientos de inventario, traspasos confirmados, incidencias, mermas y cortes de caja. Si RubiNOVA no responde, los eventos se reintentan solos sin perder información.",
    placeholderUrl: "https://…/api/rubipos",
  },
  expansion: {
    Icon: Rocket,
    titulo: "Integración Expansión Rubí · Ventas, cortes y traspasos",
    desc: "RubiPOS envía en tiempo real a Expansión Rubí (rep-leads): ventas completadas (/ventas), cortes de caja (/cortes) y traspasos enviados/recibidos con incidencias (/traspasos). Los envíos fallidos se reintentan automáticamente sin pérdida de datos.",
    placeholderUrl: "https://rep-leads.emergent.host/api/rubipos",
  },
};

export default function RubiNovaConfig({ target = "rubinova", mostrarCola = true }) {
  const preset = PRESETS[target] || PRESETS.rubinova;
  const { Icon } = preset;
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ base_url: "", api_key: "", activo: false });
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState(null);
  const [cola, setCola] = useState({ conteo: {}, items: [] });
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/integraciones/${target}/config`);
      setCfg(data);
      setForm({ base_url: data.base_url || "", api_key: "", activo: !!data.activo, stock_push: !!data.stock_push });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [target]);
  const cargarCola = useCallback(() => { if (mostrarCola) api.get("/integraciones/sync/cola").then((r) => setCola(r.data)).catch(() => {}); }, [mostrarCola]);

  useEffect(() => { cargar(); cargarCola(); }, [cargar, cargarCola]);
  useEffect(() => { if (!mostrarCola) return; const id = setInterval(cargarCola, 15000); return () => clearInterval(id); }, [cargarCola, mostrarCola]);

  async function guardar() {
    setGuardando(true);
    try {
      const payload = { base_url: form.base_url, activo: form.activo, stock_push: !!form.stock_push };
      if (form.api_key.trim()) payload.api_key = form.api_key.trim();
      await api.put(`/integraciones/${target}/config`, payload);
      toast.success("Configuración guardada");
      setForm({ ...form, api_key: "" });
      cargar();
    } catch (e) { toast.error(formatApiError(e)); } finally { setGuardando(false); }
  }

  async function probar() {
    setProbando(true); setPrueba(null);
    try {
      const { data } = await api.post(`/integraciones/${target}/probar`);
      setPrueba(data);
      data.ok ? toast.success("Conexión correcta") : toast.error(`Respondió HTTP ${data.status}`);
    } catch (e) { setPrueba({ ok: false, status: "error" }); toast.error(formatApiError(e)); } finally { setProbando(false); }
  }

  async function reintentar() {
    try { const { data } = await api.post("/integraciones/sync/cola/reintentar", {}); toast.success(`${data.reencolados} eventos reencolados`); setTimeout(cargarCola, 1500); } catch (e) { toast.error(formatApiError(e)); }
  }

  const conteo = cola.conteo || {};
  const pendientesConError = (conteo.error || 0) + (conteo.fallido || 0);

  return (
    <section className="surface-card overflow-hidden" data-testid={`${target}-config-section`}>
      <div className="flex items-center justify-between p-4 border-b border-brand-line/70 flex-wrap gap-2">
        <h2 className="font-display font-bold text-brand-ink flex items-center gap-2"><Icon className="w-4 h-4 text-brand-amber" /> {preset.titulo}</h2>
        <span className={`chip text-[10px] ${cfg?.activo ? "chip-emerald" : "chip-ink"}`} data-testid={`${target}-estado-chip`}>{cfg?.activo ? "Activa" : "Desactivada"}</span>
      </div>

      <div className="p-4 space-y-4">
        <p className="text-xs text-brand-slate">{preset.desc}</p>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-brand-muted">Base URL de la API entrante</label>
            <input data-testid={`${target}-baseurl-input`} value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} className="field w-full block mt-1 font-mono text-xs" placeholder={preset.placeholderUrl} />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-brand-muted">API Key (X-API-Key) {cfg?.tiene_key && <span className="text-brand-muted normal-case font-normal">· actual {cfg.api_key_mask}</span>}</label>
            <input data-testid={`${target}-apikey-input`} value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className="field w-full block mt-1 font-mono text-xs" placeholder={cfg?.tiene_key ? "Dejar vacío para conservar la actual" : "Pega aquí la API key"} />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-brand-ink cursor-pointer">
          <input data-testid={`${target}-activo-toggle`} type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} className="w-4 h-4 accent-brand-amber" />
          Activar la sincronización automática
        </label>
        {target === "rubinova" && (
          <div className="flex items-start gap-2 text-sm text-brand-ink rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid="rubinova-stock-push-permanente">
            <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
            <span>Envío de stock a RubiNOVA <b>permanente y automático</b> (push a <code className="font-mono text-xs">POST {form.base_url || "…"}/stock</code> en cada movimiento + fotografía completa diaria).<span className="block text-[11px] text-brand-muted">No requiere activación.</span></span>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button data-testid={`${target}-guardar`} onClick={guardar} disabled={guardando} className="btn-amber h-10 px-4 text-sm">{guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
          <button data-testid={`${target}-probar`} onClick={probar} disabled={probando} className="btn-ink h-10 px-4 text-sm">{probando ? <Loader2 className="w-4 h-4 animate-spin text-brand-amber" /> : <Wifi className="w-4 h-4 text-brand-amber" />} Probar conexión</button>
          {prueba && <span data-testid={`${target}-prueba-resultado`} className={`chip ${prueba.ok ? "chip-emerald" : "chip-red"}`}>{prueba.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />} {prueba.ok ? "Ping OK" : `Error ${prueba.status}`}</span>}
        </div>

        {target === "expansion" && cfg?.activo && <ReenvioExpansion />}
        {target === "rubinova" && <StockShopifyPanel />}
        {target === "rubinova" && null}

        {mostrarCola && (
          <div className="rounded-2xl border border-brand-line/70 p-3" data-testid="sync-cola-monitor">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="text-[10px] uppercase font-bold text-brand-muted">Cola de sincronización (RubiNOVA + Expansión)</div>
              <div className="flex gap-2">
                <button data-testid="sync-cola-refrescar" onClick={cargarCola} className="btn-soft h-8 px-3 text-xs"><RefreshCw className="w-3.5 h-3.5" /> Actualizar</button>
                {pendientesConError > 0 && <button data-testid="sync-cola-reintentar" onClick={reintentar} className="btn-amber h-8 px-3 text-xs">Reintentar {pendientesConError} con error</button>}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {[["pendiente", "Pendientes"], ["enviado", "Enviados"], ["error", "Con error"], ["fallido", "Fallidos"]].map(([k, lab]) => (
                <div key={k} className={`rounded-xl border p-2 text-center ${k === "enviado" ? "border-emerald-200 bg-emerald-50" : (conteo[k] ? "border-brand-line bg-brand-canvas" : "border-brand-line/60")}`} data-testid={`sync-conteo-${k}`}>
                  <div className="text-lg font-bold text-brand-ink font-mono">{conteo[k] || 0}</div>
                  <div className="text-[10px] text-brand-muted">{lab}</div>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sync-cola-tabla"><thead><tr className="table-head"><th className="px-2 py-1.5 text-left">Destino</th><th className="px-2 py-1.5 text-left">Recurso</th><th className="px-2 py-1.5 text-left">ID externo</th><th className="px-2 py-1.5 text-center">Estado</th><th className="px-2 py-1.5 text-center">Intentos</th><th className="px-2 py-1.5 text-left">Último error</th></tr></thead>
                <tbody>{(cola.items || []).map((it) => (
                  <tr key={it.id} className="border-t border-brand-line/50">
                    <td className="px-2 py-1.5"><span className="chip chip-ink text-[9px]">{it.target === "expansion" ? "Expansión" : "RubiNOVA"}</span></td>
                    <td className="px-2 py-1.5 font-mono">{it.resource}</td>
                    <td className="px-2 py-1.5 font-mono text-brand-muted">{it.external_id || "—"}</td>
                    <td className="px-2 py-1.5 text-center"><span className={`chip text-[9px] ${ESTADO_CHIP[it.estado] || "chip-ink"}`}>{it.estado}</span></td>
                    <td className="px-2 py-1.5 text-center font-mono">{it.intentos}{it.codigo_http ? ` · ${it.codigo_http}` : ""}</td>
                    <td className="px-2 py-1.5 text-rose-700 max-w-[280px] truncate">{it.ultimo_error || ""}</td>
                  </tr>
                ))}
                  {(cola.items || []).length === 0 && <tr><td colSpan={6} className="px-2 py-5 text-center text-brand-muted">Sin eventos en la cola. Se llenará al registrar ventas, cortes, mermas o traspasos.</td></tr>}</tbody></table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
